import {
  analyzeVideoForCutsAndFaces,
  ASSET_VERSION,
  checkVideoAnalysisSupport,
  prefetchVideoAnalysisAssets,
  toFaceTracksJson,
  toScenesCsv,
  VideoAnalysisError,
} from '@arborstoryorg/video-analysis';

import type {
  AnalysisProgress,
  FaceTracksJson,
  VideoAnalysisErrorCode,
} from '@arborstoryorg/video-analysis';

/**
 * Same origin: `video-analysis-assets` copies the models and runtime into
 * `public/video-analysis/` on every build (package.json `prebuild`/`prestart`).
 */
export const VIDEO_ANALYSIS_ASSET_BASE_URL = `/video-analysis/${ASSET_VERSION}/`;

/**
 * The detector settings this app runs (the package defaults: PySceneDetect's
 * ContentDetector plus dissolve/fade detection). Stored with the results so
 * the backend can record them in `input.json`.
 */
export type ClientSceneDetectionParams = {
  threshold: number;
  minSceneLength: number;
  detectTransitions: boolean;
};

export const CLIENT_SCENE_DETECTION_PARAMS: ClientSceneDetectionParams = {
  threshold: 27,
  minSceneLength: 15,
  detectTransitions: true,
};

export const CLIENT_FACE_DETECTION_PARAMS = {
  detectionThreshold: 0.7,
  maxYaw: 0.5,
};

export type ClientVideoAnalysisSummary = {
  backend: 'webgpu' | 'wasm';
  gpuVendor: string | null;
  durationSec: number;
  width: number;
  height: number;
  codec: string | null;
  elapsedMs: number;
  framesDecoded: number;
  framesSampled: number;
  facesDetected: number;
  sceneCount: number;
  peopleCount: number;
};

export type ClientVideoAnalysisFailureCode = VideoAnalysisErrorCode | 'aborted';

export type ClientVideoAnalysisOutcome =
  | {
      status: 'completed';
      scenesCsv: string;
      faceTracks: FaceTracksJson;
      summary: ClientVideoAnalysisSummary;
    }
  | {
      status: 'skipped';
      reasons: string[];
      codec: string | null;
    }
  | {
      status: 'failed';
      code: ClientVideoAnalysisFailureCode;
      message: string;
    };

type Options = {
  signal: AbortSignal;
  onProgress: (progress: AnalysisProgress) => void;
};

/**
 * Detect scene cuts and faces in a local video file (PLAT-12104 / PLAT-12105).
 * Never throws: an unsupported browser or codec is `skipped`, everything else
 * that goes wrong is `failed`, and in both cases the server's detection Lambdas
 * produce the results instead. Results are only returned complete.
 */
export async function runClientVideoAnalysis(
  file: File,
  { signal, onProgress }: Options,
): Promise<ClientVideoAnalysisOutcome> {
  try {
    const support = await checkVideoAnalysisSupport(file);
    if (!support.supported || !support.backend) {
      return {
        status: 'skipped',
        reasons: support.reasons,
        codec: support.codec ?? null,
      };
    }
    const result = await analyzeVideoForCutsAndFaces(file, {
      assetBaseUrl: VIDEO_ANALYSIS_ASSET_BASE_URL,
      signal,
      onProgress,
      scenes: CLIENT_SCENE_DETECTION_PARAMS,
      faces: CLIENT_FACE_DETECTION_PARAMS,
      thumbnails: false,
    });
    return {
      status: 'completed',
      scenesCsv: toScenesCsv(result),
      faceTracks: toFaceTracksJson(result),
      summary: {
        backend: result.stats.backend,
        gpuVendor: support.webgpu.adapter?.vendor ?? null,
        durationSec: result.video.duration,
        width: result.video.width,
        height: result.video.height,
        codec: result.video.codec,
        elapsedMs: result.stats.elapsedMs,
        framesDecoded: result.stats.framesDecoded,
        framesSampled: result.stats.framesSampled,
        facesDetected: result.stats.facesDetected,
        sceneCount: result.scenes.length,
        peopleCount: result.people.filter(p => !p.background).length,
      },
    };
  } catch (error) {
    if (signal.aborted) {
      return { status: 'failed', code: 'aborted', message: 'Aborted' };
    }
    if (error instanceof VideoAnalysisError) {
      return { status: 'failed', code: error.code, message: error.message };
    }
    return {
      status: 'failed',
      code: 'FAILED',
      message: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * One 0–100 value for the toast and the Story Manager row: downloading the
 * models (instant once cached) is the first 10 %, the analysis the rest.
 */
export function toOverallPercent(progress: AnalysisProgress): number {
  if (progress.phase === 'loading-models') {
    const loaded =
      progress.modelBytesTotal > 0
        ? progress.modelBytesLoaded / progress.modelBytesTotal
        : 0;
    return Math.min(10, loaded * 10);
  }
  return 10 + Math.min(1, Math.max(0, progress.fraction)) * 90;
}

/** Warm the HTTP cache with the models and runtime. Runs nothing; never rejects. */
export function prefetchClientVideoAnalysisAssets(): void {
  void prefetchVideoAnalysisAssets(VIDEO_ANALYSIS_ASSET_BASE_URL);
}
